import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { isNavigationFailure, NavigationFailureType } from 'vue-router';
import OktaVue from '@okta/okta-vue';
import App from './App.vue';
import router from './router';
import oktaAuth from './config/okta';
import { initFullStory } from './config/fullstory';
import { initRollbar, installRollbarVueHandler, reportError } from './config/rollbar';
import '@barbri/oneux';
import { connectRouter } from '@barbri/oneux';
import './config/alert-dismiss';
import '@barbri/oneux/global-styles.css';
import './styles/index.css';

// First, so errors thrown during the rest of startup are reported.
initRollbar();
initFullStory();

// Surface unexpected navigation errors instead of letting them become unhandled
// promise rejections. NavigationDuplicated (clicking a link to the current
// route) and NavigationCancelled (a second navigation interrupting the first)
// are expected outcomes of normal user behavior and are dropped silently.
router.onError((err, to) => {
  if (
    isNavigationFailure(err, NavigationFailureType.duplicated) ||
    isNavigationFailure(err, NavigationFailureType.cancelled)
  ) {
    return;
  }
  // Stale-deploy chunk-hash mismatch: the cached index.html references a
  // hashed asset filename (e.g., BeaconAuditBatchesView-C2h_p4vx.js) that no
  // longer exists on the server because a deploy replaced it with a new hash.
  // The dynamic import() in the route's lazy loader fails with a 404, the
  // navigation rejects, and the click appears dead until a hard refresh.
  // Recover by hard-navigating to the target URL — that fetches the new
  // index.html with current chunk hashes and the route resolves normally.
  const message = (err as Error)?.message ?? '';
  const isChunkLoadError =
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module');
  if (isChunkLoadError && to?.fullPath) {
    console.warn('[router] chunk-load failed (likely stale deploy); reloading to', to.fullPath);
    window.location.assign(to.fullPath);
    return;
  }
  console.error('[router] navigation error', err);
  // RouterLink and the initial navigation swallow the rejection, so this is
  // the only place these errors surface.
  reportError(err, { route: to?.path });
});

// Vite's module preload can fail BEFORE the route navigation begins (e.g.,
// when <link rel="modulepreload"> in index.html points at a hashed chunk
// that 404s post-deploy). The router.onError handler above only catches
// failures that happen during navigation; this catches the earlier ones.
window.addEventListener('vite:preloadError', (event) => {
  console.warn('[vite] module preload failed (likely stale deploy); reloading', event);
  window.location.reload();
});

const app = createApp(App);
installRollbarVueHandler(app);

app.use(createPinia());
app.use(router);
connectRouter(router);
app.use(OktaVue, {
  oktaAuth,
  onAuthRequired: () => {
    router.push('/sign-in');
  },
  onAuthResume: () => {
    router.push('/sign-in');
  },
});

app.mount('#app');
